import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import config from "../config";
import axios from "axios";

const ClientSelection = () => {
  const dispatch = useDispatch();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [tabValue, setTabValue] = useState(0);
  const { API_BASE_URL } = config;

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [shareLink, setShareLink] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [selectedClientForAction, setSelectedClientForAction] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);

  const token = useSelector((state) => state.auth?.token || state.user?.token);
  const userId = useSelector(
    (state) => state.auth?.user?.id || state.user?.id || state.auth?.userId
  );
  const selectedClient = useSelector((state) => state.client?.selectedClient);

  const apiCall = async (endpoint, options = {}) => {
    try {
      const { method = "GET", body, ...otherOptions } = options;

      const response = await axios({
        method,
        url: `${API_BASE_URL}${endpoint}`,
        data: body,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...otherOptions.headers,
        },
      });

      return response.data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      throw new Error(errorMessage);
    }
  };

  
const fetchAllClients = async () => {
  try {
    setLoading(true);

    const responseData = await apiCall("/api/v1/my-clients");
    
    
    let clientsArray = [];
    if (responseData.data && Array.isArray(responseData.data.clients)) {
      clientsArray = responseData.data.clients;
    } else if (Array.isArray(responseData.clients)) {
      clientsArray = responseData.clients;
    } 
    const formattedClients = clientsArray.map((item) => ({
    
      _id: item.allocationId,
      allocationId: item.allocationId,

     
      userId: item.user?.id, 
      username: item.user?.username,
      name: item.user?.username,
      email: item.user?.email,
      phone: item.user?.phone,

      
      status: item.connectionStatus || "active",
      isActive: item.connectionStatus === "active" || !!item.connectedAt,
      verified: item.verified || false,
      verifiedAt: item.verifiedAt,
      connectedDate: item.connectedAt,
      invitedDate: item.connectedAt,

      
      source: item.assignedBy?.id !== userId ? "admin" : "self",
      assignedBy: item.assignedBy?.username,

      connectionMethod: item.connectionMethod,
      connectionRequestId: item.connectionRequestId,
    }));

    setClients(formattedClients);
  } catch (error) {
    showNotification("Error fetching clients", "error");
    console.error(error);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (token && userId) {
      fetchAllClients();
    }
  }, [token, userId]);

  const showNotification = (message, severity = "info") => {
    setNotification({ open: true, message, severity });
  };

  const handleClientSelect = (client) => {
    dispatch({ type: "client/setSelectedClient", payload: client });
    dispatch({ type: "client/setActiveClient", payload: client });
    showNotification(
      `Selected client: ${client.username || client.name}`,
      "success"
    );
  };

  const handleInviteClient = async () => {
    if (!inviteEmail) {
      showNotification("Please enter an email address", "warning");
      return;
    }
    try {
      await apiCall("/api/v1/professional/invite", {
        method: "POST",
        body: {
          recipientEmail: inviteEmail,
          message: inviteMessage,
        },
      });
      showNotification(`Invitation sent to ${inviteEmail}`, "success");
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteMessage("");
      fetchAllClients();
    } catch (error) {
      showNotification(error.message || "Error sending invitation", "error");
    }
  };

  const handleGenerateShareLink = async () => {
    try {
      const responseData = await apiCall("/api/v1/generate-link", {
        method: "POST",
        body: {
          expiryDays: 7,
          message: "",
        },
      });
      if (responseData.data && responseData.data.link) {
        setShareLink(responseData.data.link);
        showNotification("Share link generated successfully", "success");
      } else {
        throw new Error("Response did not contain a link.");
      }
    } catch (error) {
      showNotification(error.message || "Error generating share link", "error");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    showNotification("Link copied to clipboard", "success");
  };

  const handleVerifyClient = async () => {
    if (verificationCode.length !== 6) {
      showNotification("Please enter a valid 6-digit code", "warning");
      return;
    }
    try {
      await apiCall("/api/v1/verify", {
        method: "POST",
        body: {
          allocationId: selectedClientForAction.allocationId,
          verificationCode: verificationCode,
        },
      });
      showNotification("Client verified successfully", "success");
      setVerifyDialogOpen(false);
      setVerificationCode("");
      fetchAllClients();
    } catch (error) {
      showNotification(error.message || "Error verifying client", "error");
    }
  };

  const handleResendInvitation = async (client) => {
    try {
      if (!client.connectionRequestId) {
        showNotification("Cannot resend: connection request ID not found.", "error");
        return;
      }
      await apiCall(`/api/v1/resend/${client.connectionRequestId}`, {
        method: "POST",
      });
      showNotification(`Invitation resent to ${client.email}`, "success");
    } catch (error) {
      showNotification(error.message || "Error resending invitation", "error");
    }
    handleMenuClose();
  };

  const handleRevokeAccess = async (client) => {
    if (client.source === "admin") {
      showNotification("Cannot revoke admin-assigned clients.", "warning");
      handleMenuClose();
      return;
    }
    try {
      const allocationId = client.allocationId || client._id;
      await apiCall(`/api/v1/revoke/${allocationId}`, { method: "DELETE" });
      showNotification(`Access revoked for ${client.username || client.name}`, "success");
      fetchAllClients();
    } catch (error) {
      showNotification(error.message || "Error revoking access", "error");
    }
    handleMenuClose();
  };

  const handleMenuOpen = (event, client) => {
    setAnchorEl(event.currentTarget);
    setSelectedClientForAction(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const filteredClients = clients.filter((client) => {
    const name = client.username || client.name || "";
    const email = client.email || "";
    const matchesSearch =
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase());

    if (tabValue === 0) return matchesSearch;
    if (tabValue === 1)
      return (
        matchesSearch &&
        (client.status === "active" || client.isActive || client.verified)
      );
    if (tabValue === 2) return matchesSearch && client.status === "pending";
    if (tabValue === 3)
      return (
        matchesSearch && (client.source === "admin" || client.source === "both")
      );

    return matchesSearch;
  });

  const getStatusChip = (client) => {
    if (client.verified || client.status === "active" || client.isActive) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Active"
          color="success"
          size="small"
        />
      );
    }
    if (client.status === "pending") {
      return (
        <Chip
          icon={<PendingIcon />}
          label="Pending"
          color="warning"
          size="small"
        />
      );
    }
    return null;
  };

  const getSourceBadge = (client) => {
    if (client.source === "admin" || client.source === "both") {
      return (
        <Tooltip title="Assigned by Administrator">
          <Chip
            icon={<AdminPanelSettingsIcon />}
            label="Admin"
            size="small"
            color="info"
            variant="outlined"
          />
        </Tooltip>
      );
    }
    return null;
  };

  const activeCount = clients.filter(
    (c) => c.status === "active" || c.isActive || c.verified
  ).length;
  const pendingCount = clients.filter((c) => c.status === "pending").length;
  const adminAssignedCount = clients.filter(
    (c) => c.source === "admin" || c.source === "both"
  ).length;

  if (loading && clients.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", mt: 4, px: 2 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Client Management
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage your professional client connections
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => setShareDialogOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Share Link
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteDialogOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Add Client
            </Button>
          </Box>
        </Box>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography variant="h6">{activeCount}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Active Connections
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <PendingIcon color="warning" />
                  <Box>
                    <Typography variant="h6">{pendingCount}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Pending Invitations
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <AdminPanelSettingsIcon color="info" />
                  <Box>
                    <Typography variant="h6">{adminAssignedCount}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Admin Assigned
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <GroupAddIcon color="primary" />
                  <Box>
                    <Typography variant="h6">{clients.length}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Clients
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {clients.length > 0 && (
          <>
            <Box mb={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label={`All (${clients.length})`} sx={{ textTransform: "none" }} />
                <Tab label={`Active (${activeCount})`} sx={{ textTransform: "none" }} />
                <Tab label={`Pending (${pendingCount})`} sx={{ textTransform: "none" }} />
                <Tab label={`Admin Assigned (${adminAssignedCount})`} sx={{ textTransform: "none" }}/>
              </Tabs>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search clients by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
          </>
        )}
      </Paper>

      <Paper elevation={3} sx={{ p: 2 }}>
        {clients.length === 0 ? (
          <Box textAlign="center" py={8}>
            <BusinessCenterIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}/>
            <Typography variant="h6" color="textSecondary" gutterBottom fontWeight={500}>
              No Clients Yet
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3} maxWidth={400} mx="auto">
              Start building your professional network by adding your first
              client. Send invitations via email or share a connection link.
            </Typography>
            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteDialogOpen(true)}
                size="large"
                sx={{ textTransform: "none" }}
              >
                Add Your First Client
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => setShareDialogOpen(true)}
                size="large"
                sx={{ textTransform: "none" }}
              >
                Generate Share Link
              </Button>
            </Box>
          </Box>
        ) : filteredClients.length === 0 ? (
          <Box textAlign="center" py={6}>
            <SearchIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography color="textSecondary" gutterBottom>
              No clients match your search criteria
            </Typography>
            <Button variant="text" onClick={() => setSearchTerm("")} sx={{ mt: 2, textTransform: "none" }}>
              Clear Search
            </Button>
          </Box>
        ) : (
          <List sx={{ maxHeight: "500px", overflow: "auto" }}>
            {filteredClients.map((client, index) => (
              <React.Fragment key={client._id}>
                <ListItem
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    cursor: "pointer",
                    ...(selectedClient?._id === client._id && {
                      bgcolor: "action.selected",
                    }),
                  }}
                  onClick={() => handleClientSelect(client)}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        client.verified || client.isActive ? (
                          <VerifiedUserIcon
                            sx={{
                              bgcolor: "white",
                              borderRadius: "50%",
                              fontSize: 16,
                              color: "success.main",
                            }}
                          />
                        ) : null
                      }
                    >
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        <PersonIcon />
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle1">
                          {client.username || client.name || "Unnamed Client"}
                        </Typography>
                        {getStatusChip(client)}
                        {getSourceBadge(client)}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography component="span" variant="body2" color="textPrimary">
                          {client.email}
                        </Typography>
                        {client.phone && ` · ${client.phone}`}
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          {client.source === "admin" || client.source === "both"
                            ? `Assigned by ${client.assignedBy || "Administrator"} · `
                            : ""}
                          {client.status === "active" || client.isActive
                            ? `Connected ${
                                client.connectedDate
                                  ? new Date(
                                      client.connectedDate
                                    ).toLocaleDateString()
                                  : ""
                              }`
                            : client.invitedDate
                            ? `Invited on ${new Date(
                                client.invitedDate
                              ).toLocaleDateString()}`
                            : "Pending connection"}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
                    {client.status === "active" &&
                      !client.verified &&
                      client.source !== "admin" && (
                        <Tooltip title="Verify Client">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedClientForAction(client);
                              setVerifyDialogOpen(true);
                            }}
                          >
                            <VerifiedUserIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, client)}>
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </ListItem>
                {index < filteredClients.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedClientForAction?.status === "pending" &&
          selectedClientForAction?.source !== "admin" && (
            <MenuItem onClick={() => handleResendInvitation(selectedClientForAction)}>
              <ListItemIcon>
                <RefreshIcon fontSize="small" />
              </ListItemIcon>
              Resend Invitation
            </MenuItem>
          )}
        <MenuItem
          onClick={() => handleRevokeAccess(selectedClientForAction)}
          disabled={selectedClientForAction?.source === "admin"}
        >
          <ListItemIcon>
            <CancelIcon
              fontSize="small"
              color={
                selectedClientForAction?.source === "admin"
                  ? "disabled"
                  : "error"
              }
            />
          </ListItemIcon>
          <Typography
            color={
              selectedClientForAction?.source === "admin"
                ? "textSecondary"
                : "error"
            }
          >
            {selectedClientForAction?.source === "admin"
              ? "Cannot Revoke (Admin)"
              : "Revoke Access"}
          </Typography>
        </MenuItem>
      </Menu>

      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Invite Client</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Client Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="client@example.com"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Personal Message (Optional)"
              multiline
              rows={3}
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
              placeholder="Add a personal message to your invitation..."
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              An invitation email will be sent with a secure connection link.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={handleInviteClient}
            sx={{ textTransform: "none" }}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setShareLink("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon color="primary" />
            <Typography variant="h6">Share Connection Link</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Generate a secure link that clients can use to connect with you. Links expire after 7 days.
            </Typography>
            {shareLink ? (
              <>
                <TextField
                  fullWidth
                  value={shareLink}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleCopyLink}>
                          <LinkIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <Alert severity="success">
                  Share this link with your clients to connect instantly.
                </Alert>
              </>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleGenerateShareLink}
                size="large"
                sx={{ textTransform: 'none' }}
              >
                Generate Share Link
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setShareDialogOpen(false);
              setShareLink("");
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VerifiedUserIcon color="primary" />
            <Typography variant="h6">Verify Client Connection</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Enter the 6-digit verification code sent to{" "}
              <strong>{selectedClientForAction?.email}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                setVerificationCode(value);
              }}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: "center",
                  fontSize: "1.5rem",
                  letterSpacing: "0.5rem",
                },
              }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              The verification code ensures secure client connections.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerifyDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<VerifiedUserIcon />}
            onClick={handleVerifyClient}
            disabled={verificationCode.length !== 6}
            sx={{ textTransform: "none" }}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientSelection;
